import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, CalendarClock, Building2, Gauge, Scale, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Seo } from "@/components/Seo";

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.05 } }),
};

const TIERS = [
  { key: "standard_assessment", self: true, name: "Standard Assessment", price: "$1,500",
    lookback: "6-Month Lookback", vendors: "Up to 3 Vendors / Channels",
    best: "For a smaller or more concentrated marketing program.", cta: "Start Your Assessment",
    items: ["6-month marketing performance review", "Review of up to 3 vendors / channels", "Marketing spend reconciliation", "Vendor invoice & performance review", "Lead, inquiry & retained-case analysis", "Cost-per-retained-case where data allows", "Executive findings report & consultation"] },
  { key: "expanded_assessment", self: true, highlight: true, name: "Expanded Assessment", price: "$2,500",
    lookback: "12-Month Lookback", vendors: "Up to 5 Vendors / Channels",
    best: "A deeper view of where marketing dollars went and what they produced.", cta: "Start Your Assessment",
    items: ["Everything in Standard, plus:", "Full 12-month historical review", "Analysis of up to 5 vendors / channels", "Year-over-year performance patterns", "Deeper vendor comparisons & allocation analysis", "Lead-to-retained-case conversion analysis", "Expanded findings & recommendations"] },
  { key: "comprehensive_assessment", name: "Comprehensive Assessment", pricePrefix: "Starting at", price: "$3,500",
    lookback: "12–18 Month Lookback", vendors: "Up to 8 Vendors / Channels",
    best: "For larger budgets, multiple agencies and complex ecosystems.", cta: "Request Comprehensive Assessment",
    items: ["12–18 months of marketing history", "Review of up to 8 vendors / channels", "Vendor-by-vendor & cross-channel analysis", "Lead-source attribution review", "Cost-per-lead & cost-per-retained-case", "Vendor accountability scorecard", "Comprehensive report & strategy session"] },
  { key: "enterprise_assessment", name: "Enterprise Assessment", price: "Custom",
    lookback: "Custom Lookback", vendors: "9+ Vendors / Channels",
    best: "For large or complex marketing operations.", cta: "Request Custom Assessment",
    items: ["Custom historical lookback", "9+ marketing vendors / channels", "Multi-office / multi-market analysis", "Large-scale spend reconciliation", "Complex attribution & portfolio analysis", "Executive-level custom reporting", "Leadership presentation & recommendations"] },
];

const FACTORS = [
  { icon: CalendarClock, t: "Lookback Period", d: "How much historical marketing activity we analyze." },
  { icon: Building2, t: "Vendors & Channels", d: "How many agencies, lead generators, platforms and marketing sources are included." },
  { icon: Gauge, t: "Complexity", d: "The volume of invoices, reports, lead data, retained-case information, markets and attribution sources." },
];

const PROCESS = [
  { n: "1", t: "Submit", d: "Securely provide marketing invoices, vendor reports, lead information and retained-case data." },
  { n: "2", t: "Reconcile", d: "We reconcile marketing spend, vendor-reported activity and actual outcomes." },
  { n: "3", t: "Analyze", d: "We identify performance gaps, discrepancies, attribution problems and areas needing scrutiny." },
  { n: "4", t: "Report", d: "Leadership receives an executive findings report with clear observations and recommendations." },
];

const ONGOING = [
  { icon: CalendarClock, name: "Quarterly Performance Review", price: "Starting at $1,000", unit: "per review", cta: "Ask About Quarterly Reviews" },
  { icon: Scale, name: "Annual Performance Review", price: "Starting at $2,000", unit: "per year", cta: "Ask About Annual Reviews" },
  { icon: ShieldCheck, name: "Executive Marketing Oversight", price: "Starting at $3,500", unit: "per month", badge: "Available by consultation", cta: "Discuss Executive Oversight" },
];

const Section = ({ children, className = "", ...rest }) => (
  <section className={`relative z-10 border-t border-white/10 ${className}`} {...rest}>
    <div className="max-w-7xl mx-auto px-6 py-16 sm:py-20">{children}</div>
  </section>
);

export default function Pricing() {
  const navigate = useNavigate();
  return (
    <div className="dark relative min-h-screen bg-[#0A1B2E] text-[#F5F5F5] font-body overflow-hidden" data-testid="pricing-page">
      <Seo title="Pricing — RetainProof Marketing Performance Assessments" description="Simple, one-time pricing based on the size of the review: Standard $1,500, Expanded $2,500, Comprehensive from $3,500, and Enterprise custom. No subscription required." path="/pricing" />
      <div className="grain-overlay" />

      <nav className="fixed top-0 inset-x-0 z-50 bg-black/60 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 font-body font-bold text-2xl tracking-tight" data-testid="brand-logo">
            <img src="/retainproof-icon-dark.png" alt="RetainProof" className="h-8 w-auto" />
            <span>Retain<span className="text-[#0E7C7B]">Proof</span><sup className="text-[0.5em] font-normal align-super">™</sup></span>
          </Link>
          <div className="flex items-center gap-1 sm:gap-3">
            <Link to="/features"><Button variant="ghost" className="hidden sm:inline-flex text-zinc-300 hover:text-white hover:bg-white/5 rounded-none">Features</Button></Link>
            <Link to="/about"><Button variant="ghost" className="hidden sm:inline-flex text-zinc-300 hover:text-white hover:bg-white/5 rounded-none">About</Button></Link>
            <Link to="/login"><Button variant="ghost" className="text-zinc-300 hover:text-white hover:bg-white/5 rounded-none">Sign in</Button></Link>
            <Button onClick={() => navigate("/register")} className="bg-[#D5A44A] text-black hover:bg-[#B8923D] rounded-none font-medium" data-testid="pricing-nav-register">Get started</Button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 pt-36 pb-14">
        <motion.p initial="hidden" animate="show" variants={fadeUp} custom={0} className="font-mono-data text-xs uppercase tracking-[0.25em] text-[#D5A44A] mb-5">Marketing Performance Assessment · Pricing</motion.p>
        <motion.h1 initial="hidden" animate="show" variants={fadeUp} custom={1} className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-tight tracking-tight max-w-4xl" data-testid="pricing-hero-title">
          Simple pricing based on the <span className="italic text-[#D5A44A]">size of the review.</span>
        </motion.h1>
        <motion.p initial="hidden" animate="show" variants={fadeUp} custom={2} className="mt-6 text-lg text-zinc-400 max-w-3xl leading-relaxed">
          Each RetainProof engagement is a one-time Marketing Performance Assessment — no subscription, no long-term agreement, and no implementation fee. Pricing is set by how much history, how many vendors and how much complexity we analyze.
        </motion.p>
        <motion.p initial="hidden" animate="show" variants={fadeUp} custom={3} className="mt-4 text-sm text-zinc-500 max-w-3xl leading-relaxed" data-testid="pricing-value-anchor">
          Most firms spend more in a single month of unverified marketing than a full assessment costs. One independent review can surface duplicated charges, weak attribution and spend that is difficult to verify — so leadership can redirect it with confidence.
        </motion.p>
        <div className="mt-8 grid sm:grid-cols-3 gap-6">
          {FACTORS.map((f) => (
            <div key={f.t} className="border border-white/10 bg-[#12324D] p-5" data-testid="pricing-factor">
              <f.icon className="h-5 w-5 text-[#D5A44A]" />
              <p className="font-display text-lg mt-3">{f.t}</p>
              <p className="text-sm text-zinc-400 mt-1 leading-relaxed">{f.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Tiers */}
      <Section className="bg-[#0B1F33]">
        <div id="tiers" className="scroll-mt-24 grid md:grid-cols-2 xl:grid-cols-4 gap-6">
          {TIERS.map((t) => (
            <div key={t.key} className={`relative border p-7 flex flex-col ${t.highlight ? "border-[#D5A44A] bg-[#14120A]" : "border-white/10 bg-[#12324D]"}`} data-testid={`pricing-tier-${t.key}`}>
              {t.highlight && <span className="absolute -top-3 left-7 bg-[#D5A44A] text-black text-[10px] font-mono-data uppercase tracking-widest px-2 py-1">Most popular</span>}
              <h3 className="font-display text-2xl tracking-tight">{t.name}</h3>
              <p className="mt-3">
                {t.pricePrefix && <span className="block text-zinc-400 text-xs">{t.pricePrefix}</span>}
                <span className="font-display text-4xl">{t.price}</span>
              </p>
              <div className="mt-4 border-y border-white/10 py-3 space-y-1">
                <p className="font-mono-data text-[11px] uppercase tracking-widest text-[#D5A44A]">{t.lookback}</p>
                <p className="font-mono-data text-[11px] uppercase tracking-widest text-[#0E7C7B]">{t.vendors}</p>
              </div>
              <p className="mt-4 text-sm text-zinc-300 leading-relaxed">{t.best}</p>
              <ul className="mt-5 space-y-2 text-sm text-zinc-400 flex-1">
                {t.items.map((it) => <li key={it} className="flex gap-2"><Check className="h-4 w-4 text-[#D5A44A] shrink-0 mt-0.5" />{it}</li>)}
              </ul>
              {t.self ? (
                <Button onClick={() => navigate(`/register?tier=${t.key}`)} className={`mt-6 w-full rounded-none h-11 text-xs sm:text-sm ${t.highlight ? "bg-[#D5A44A] text-black hover:bg-[#B8923D]" : "bg-white/10 text-white hover:bg-white/20"}`} data-testid={`pricing-cta-${t.key}`}>{t.cta}</Button>
              ) : (
                <Link to="/consultation" className="mt-6" data-testid={`pricing-cta-${t.key}`}><Button className="w-full rounded-none h-11 text-xs sm:text-sm bg-white/10 text-white hover:bg-white/20">{t.cta}</Button></Link>
              )}
            </div>
          ))}
        </div>
      </Section>

      {/* Process */}
      <Section>
        <h2 className="font-display text-3xl sm:text-4xl font-light tracking-tight mb-10">The assessment process</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {PROCESS.map((s) => (
            <div key={s.n} className="border-t-2 border-[#D5A44A] bg-[#12324D] p-6">
              <p className="font-display text-3xl text-[#D5A44A]/60">{s.n}</p>
              <p className="font-display text-xl mt-2">{s.t}</p>
              <p className="text-sm text-zinc-400 mt-2 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* After your assessment */}
      <Section className="bg-[#0B1F33]">
        <p className="font-mono-data text-[11px] uppercase tracking-[0.2em] text-[#D5A44A]">After your assessment</p>
        <h2 className="font-display text-3xl sm:text-4xl font-light tracking-tight mt-2 mb-2">Optional ongoing services</h2>
        <p className="text-base text-zinc-400 max-w-3xl mb-8 leading-relaxed">The assessment gives your firm a clear benchmark. RetainProof can also help you measure what happens after changes are made.</p>
        <div className="grid md:grid-cols-3 gap-6">
          {ONGOING.map((s) => (
            <div key={s.name} className="border border-white/10 bg-[#12324D] p-7 flex flex-col" data-testid="pricing-ongoing">
              <s.icon className="h-6 w-6 text-[#D5A44A]" />
              <h3 className="font-display text-xl mt-4 leading-tight">{s.name}</h3>
              {s.badge && <span className="mt-2 inline-block w-fit border border-[#0E7C7B]/40 text-[#4FD1CE] text-[10px] font-mono-data uppercase tracking-widest px-2 py-0.5">{s.badge}</span>}
              <p className="mt-3"><span className="font-display text-2xl">{s.price}</span><span className="text-zinc-500 text-sm"> {s.unit}</span></p>
              <Link to="/consultation" className="mt-5"><Button className="w-full rounded-none bg-white/10 text-white hover:bg-white/20 h-11 text-xs sm:text-sm">{s.cta}</Button></Link>
            </div>
          ))}
        </div>
      </Section>

      {/* CTA */}
      <Section>
        <div className="text-center max-w-3xl mx-auto">
          <h2 className="font-display text-3xl sm:text-4xl font-light tracking-tight leading-tight">Ready to see what your marketing is actually producing?</h2>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Button onClick={() => navigate("/register?tier=expanded_assessment")} className="bg-[#D5A44A] text-black hover:bg-[#B8923D] rounded-none h-12 px-8 text-base font-medium group" data-testid="pricing-final-start">
              Start Your Assessment <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Button>
            <Link to="/consultation"><Button variant="outline" className="rounded-none h-12 px-8 text-base border-white/25 text-white bg-transparent hover:bg-white/5">Schedule a Consultation</Button></Link>
          </div>
        </div>
      </Section>

      <footer className="relative z-10 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-10 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-zinc-500">
          <span>© 2026 RetainProof™ · A product of CMD Marketing Enterprises LLC</span>
          <div className="flex gap-6">
            <Link to="/" className="hover:text-white">Home</Link>
            <Link to="/about" className="hover:text-white">About</Link>
            <Link to="/features" className="hover:text-white">Features</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
